'use client';

import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Article {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  authorRole: string;
  publishDate: string;
  readTime: string;
  image: string;
  alt: string;
  tags: string[];
}

interface ArticleCardProps {
  article: Article;
  viewMode: 'grid' | 'list';
}

const ArticleCard = ({ article, viewMode }: ArticleCardProps) => {
  if (viewMode === 'list') {
    return (
      <div className="bg-white rounded-xl border border-border shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group">
        <div className="flex flex-col md:flex-row gap-0">
          <div className="relative w-full md:w-64 h-48 md:h-auto overflow-hidden flex-shrink-0">
            <AppImage
              src={article.image}
              alt={article.alt}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
          <div className="p-6 flex-1">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-3 py-1 bg-primary/10 text-primary font-bold rounded-full text-xs">
                {article.category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </span>
              <span className="text-muted-foreground text-sm">•</span>
              <span className="text-muted-foreground text-sm">{article.readTime}</span>
            </div>
            <h3 className="font-heading text-xl font-black text-foreground mb-3 group-hover:text-primary transition-colors line-clamp-2">
              {article.title}
            </h3>
            <p className="text-muted-foreground mb-4 line-clamp-2">
              {article.excerpt}
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-sm font-black">
                  {article.author.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <p className="font-bold text-foreground text-sm">{article.author}</p>
                  <p className="text-xs text-muted-foreground">{new Date(article.publishDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </div>
              </div>
              <button className="px-4 py-2 bg-primary text-white font-bold rounded-lg hover:bg-secondary transition-all duration-200 flex items-center gap-2">
                Read
                <Icon name="ArrowRightIcon" size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-border shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={article.image}
          alt={article.alt}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
        />
        <div className="absolute top-3 left-3">
          <span className="px-3 py-1 bg-primary text-white font-bold rounded-full text-xs shadow-lg">
            {article.category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
          </span>
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <Icon name="ClockIcon" size={16} />
          <span>{article.readTime}</span>
          <span>•</span>
          <span>{new Date(article.publishDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
        </div>
        <h3 className="font-heading text-lg font-black text-foreground mb-3 group-hover:text-primary transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-xs font-black">
              {article.author.split(' ').map(n => n[0]).join('')}
            </div>
            <p className="font-bold text-foreground text-sm">{article.author}</p>
          </div>
          <button className="text-primary font-bold text-sm hover:text-secondary transition-colors flex items-center gap-1">
            Read
            <Icon name="ArrowRightIcon" size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArticleCard;